import {
  formatDecimal,
  formatTonnage,
  isPlainObject,
  normalizeText,
  toNumber,
} from "./pageDataHelpers.js";
import { appendDispatchDateToUrl, enrichResultsUrlWithStoredDispatchContext } from "./dispatchDateUrls.js";
import { resolveOptimizationStatusPresentation } from "./optimizationStatusPresentation.js";
import { normalizeFleetUtilization, fleetUtilizationReportFields } from "./fleetUtilizationData.js";
import { normalizeDashboardLowLoad } from "./dashboardLowLoad.js";

const DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});
const TIME_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  hour: "2-digit",
  minute: "2-digit",
});
const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "short",
});
const DAY_MONTH_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "2-digit",
});
const CURRENCY_FORMATTER = new Intl.NumberFormat("fr-CA", {
  style: "currency",
  currency: "CAD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const WEEKDAY_LABELS = ["DI", "LU", "MA", "ME", "JE", "VE", "SA"];
const DEFAULT_LOW_LOAD_THRESHOLD_PCT = 25;

const EMPTY_DASHBOARD = {
  orders: {
    total: 0,
    assigned: 0,
    pending: 0,
  },
  trucks: {
    total: 0,
    active: 0,
    mobilized: 0,
    available: 0,
    list: [],
  },
  tonnage: {
    planned: 0,
    coverage_pct: 0,
  },
  alerts: {
    count: 0,
    suspended: 0,
    errors: 0,
  },
};

const EMPTY_ANALYTICS_DAY = {
  activeTruckCount: 0,
  avgAssignedDurationLabel: "0 min",
  avgAssignedDurationMinutes: 0,
  avgBusinessStopCountPerUsedTruck: 0,
  avgBusinessStopCountPerUsedTruckLabel: "0",
  avgCompartmentFillLabel: "0 %",
  avgCompartmentFillPct: 0,
  avgCompartmentUsageLabel: "0 %",
  avgCompartmentUsagePct: 0,
  avgCompartmentsUsed: 0,
  avgCompartmentsUsedLabel: "0",
  avgDistancePerUsedTruckKm: 0,
  avgDistancePerUsedTruckLabel: "0 km",
  avgHighLoadDriveShareLabel: "0 %",
  avgHighLoadDriveSharePct: 0,
  avgMaxWorkLabel: null,
  avgMaxWorkMinutes: null,
  avgPeakCompartmentOccupancyLabel: "0 %",
  avgPeakCompartmentOccupancyPct: 0,
  avgPeakCompartmentsUsed: 0,
  avgPeakCompartmentsUsedLabel: "0",
  avgPeakLoadFillRateLabel: "n/d",
  avgPeakLoadFillRatePct: null,
  avgTonsPerDeliveryStopLabel: "0 t",
  avgTonsPerDeliveryStopTons: 0,
  avgTimeBudgetConsumedLabel: "0 %",
  avgTimeBudgetConsumedPct: 0,
  avgTonnagePerUsedTruckLabel: "0 t",
  avgTonnagePerUsedTruckTons: 0,
  compartmentFillRateLabel: "0 %",
  compartmentFillRatePct: 0,
  compartmentNote: "Aucun compartiment actif",
  dateLabel: "",
  dayLabel: "",
  dispatchEstimatedCostCad: 0,
  dispatchEstimatedCostLabel: "0 $",
  dispatchOrderCount: 0,
  dispatchProductCount: 0,
  preloadTruckCount: 0,
  preloadTruckLabel: "0 %",
  preloadTruckPct: 0,
  priorityWindowTruckCount: 0,
  priorityWindowTruckLabel: "0 %",
  priorityWindowTruckPct: 0,
  protectedOrderCount: 0,
  protectedOrderLabel: "0 %",
  protectedOrderPct: 0,
  protectedOrderTotalCount: 0,
  dispatchDate: null,
  deliveryVisitCount: 0,
  fleetWorkedMinutes: null,
  fleetMaxWorkMinutes: null,
  fleetTimeUtilizationPct: null,
  fleetUnproductiveMinutes: null,
  fleetUnproductivePct: null,
  gaugeRatio: 0,
  hasDispatch: false,
  hasSnapshot: false,
  fleetTruckCount: 0,
  fleetUsagePct: 0,
  plannedTonnageLabel: "0 t",
  plannedTonnageTons: 0,
  totalCompartments: 0,
  usedCompartments: 0,
  usedFleetLabel: "0 %",
  usedFleetNote: "Aucun camion actif",
  usedFleetPct: 0,
  usedTruckCount: 0,
  truckOccupancyPct: 0,
  lowLoadDistanceKm: null,
  classifiedDistanceKm: null,
  lowLoadDistancePct: null,
  lowLoadThresholdPct: DEFAULT_LOW_LOAD_THRESHOLD_PCT,
};

const EMPTY_ANALYTICS = {
  current: EMPTY_ANALYTICS_DAY,
  hasHistory: false,
  historyState: "empty",
  last30Days: [],
  last7Days: [],
  defaultPeriodKey: "month",
  periodOptions: [],
  periods: {},
};

function toOptionalNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function toOptionalFiniteNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  return toOptionalNumber(value);
}

function parseAnalyticsDate(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  // A dispatch date has no timezone. Local noon preserves its civil day.
  const parsedDate = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function resolveWeekdayLabel(parsedDate, explicitLabel = "") {
  const resolvedExplicitLabel = normalizeText(explicitLabel);
  if (resolvedExplicitLabel) {
    return resolvedExplicitLabel;
  }

  if (!(parsedDate instanceof Date)) {
    return "";
  }

  return WEEKDAY_LABELS[parsedDate.getDay()] || "";
}

function resolveDateLabel(parsedDate) {
  if (!(parsedDate instanceof Date)) {
    return "";
  }

  return SHORT_DATE_FORMATTER.format(parsedDate);
}

function formatDurationCompact(minutes) {
  const numericMinutes = Math.round(Math.max(toNumber(minutes, 0), 0));
  const hours = Math.floor(numericMinutes / 60);
  const remainingMinutes = Math.round(numericMinutes - (hours * 60));

  if (hours <= 0) {
    return `${remainingMinutes} min`;
  }

  if (remainingMinutes <= 0) {
    return `${hours}h`;
  }

  return `${hours}h${String(remainingMinutes).padStart(2, "0")}`;
}

function formatPercentLabel(value, fallback = "0 %") {
  const formattedValue = formatDecimal(value, 1);
  return formattedValue === null ? fallback : `${formattedValue} %`;
}

function formatDistanceCompact(value, fallback = "0 km") {
  const formattedValue = formatDecimal(value, 1);
  return formattedValue === null ? fallback : `${formattedValue} km`;
}

function formatCurrencyCompact(value, fallback = "0 $") {
  const numericValue = toOptionalNumber(value);
  return numericValue === null ? fallback : CURRENCY_FORMATTER.format(numericValue);
}

function resolveAnalyticsDay(rawDay, fallbackDate = null, fleetTruckCount = 0) {
  const sourceDay = isPlainObject(rawDay) ? rawDay : {};
  const dispatchDate = normalizeText(sourceDay.dispatch_date) || normalizeText(fallbackDate) || null;
  const parsedDispatchDate = parseAnalyticsDate(dispatchDate);
  const avgAssignedDurationMinutes = Math.max(toNumber(sourceDay.avg_assigned_duration_minutes, 0), 0);
  const avgBusinessStopCountPerUsedTruck = Math.max(toOptionalNumber(sourceDay.avg_business_stop_count_per_used_truck) ?? 0, 0);
  const avgCompartmentsUsedValue = toOptionalNumber(sourceDay.avg_compartments_used);
  const avgCompartmentUsagePctValue = toOptionalNumber(sourceDay.avg_compartment_usage_pct);
  const avgCompartmentFillPctValue = toOptionalNumber(sourceDay.avg_compartment_fill_pct);
  const avgDistancePerUsedTruckKm = Math.max(toOptionalNumber(sourceDay.avg_distance_per_used_truck_km) ?? 0, 0);
  const avgHighLoadDriveSharePct = Math.max(toOptionalNumber(sourceDay.avg_high_load_drive_share_pct) ?? 0, 0);
  const avgMaxWorkMinutes = toOptionalNumber(sourceDay.avg_max_work_minutes);
  const avgCompartmentsUsed = avgCompartmentsUsedValue === null ? null : Math.max(avgCompartmentsUsedValue, 0);
  const avgCompartmentUsagePct = avgCompartmentUsagePctValue === null ? null : Math.max(avgCompartmentUsagePctValue, 0);
  const avgCompartmentFillPct = avgCompartmentFillPctValue === null ? null : Math.max(avgCompartmentFillPctValue, 0);
  const avgPeakCompartmentsUsed = Math.max(toOptionalNumber(sourceDay.avg_peak_compartments_used) ?? 0, 0);
  const avgPeakCompartmentOccupancyPct = Math.max(toOptionalNumber(sourceDay.avg_peak_compartment_occupancy_pct) ?? 0, 0);
  const peakLoadValue = toOptionalFiniteNumber(sourceDay.avg_peak_load_fill_rate_pct);
  const avgPeakLoadFillRatePct = peakLoadValue !== null && peakLoadValue >= 0 ? peakLoadValue : null;
  const avgTonsPerDeliveryStopTons = Math.max(toOptionalNumber(sourceDay.avg_tons_per_delivery_stop_tons) ?? 0, 0);
  const avgTimeBudgetConsumedPct = Math.max(toOptionalNumber(sourceDay.avg_time_budget_consumed_pct) ?? 0, 0);
  const avgTonnagePerUsedTruckTons = Math.max(toOptionalNumber(sourceDay.avg_tonnage_per_used_truck_tons) ?? 0, 0);
  const dispatchEstimatedCostCad = Math.max(toOptionalNumber(sourceDay.dispatch_estimated_cost_cad) ?? 0, 0);
  const plannedTonnageTons = Math.max(toNumber(sourceDay.planned_tonnage_tons, 0), 0);
  const dispatchProductCount = Math.max(toNumber(sourceDay.dispatch_product_count, 0), 0);
  const preloadTruckCount = Math.max(toNumber(sourceDay.preload_truck_count, 0), 0);
  const preloadTruckPct = Math.max(toOptionalNumber(sourceDay.preload_truck_pct) ?? 0, 0);
  const priorityWindowTruckCount = Math.max(toNumber(sourceDay.priority_window_truck_count, 0), 0);
  const priorityWindowTruckPct = Math.max(toOptionalNumber(sourceDay.priority_window_truck_pct) ?? 0, 0);
  const protectedOrderCount = Math.max(toNumber(sourceDay.protected_order_count, 0), 0);
  const protectedOrderPct = Math.max(toOptionalNumber(sourceDay.protected_order_pct) ?? 0, 0);
  const protectedOrderTotalCount = Math.max(toNumber(sourceDay.protected_order_total_count, 0), 0);
  const usedCompartments = Math.max(toNumber(sourceDay.used_compartments, 0), 0);
  const totalCompartments = Math.max(toNumber(sourceDay.total_compartments, 0), 0);
  const compartmentFillRatePct = Math.max(toOptionalNumber(sourceDay.compartment_fill_rate_pct) ?? 0, 0);
  const activeTruckCount = Math.max(toNumber(sourceDay.active_truck_count, 0), 0);
  const usedTruckCount = Math.max(toNumber(sourceDay.used_truck_count, 0), 0);
  const fleetWorkedMinutesValue = toOptionalFiniteNumber(sourceDay.fleet_worked_minutes);
  const fleetMaxWorkMinutesValue = toOptionalFiniteNumber(sourceDay.fleet_max_work_minutes);
  const fleetWorkedMinutes = fleetWorkedMinutesValue === null ? null : Math.max(fleetWorkedMinutesValue, 0);
  const fleetMaxWorkMinutes = fleetMaxWorkMinutesValue === null ? null : Math.max(fleetMaxWorkMinutesValue, 0);
  const fleetTimeUtilizationPctValue = toOptionalFiniteNumber(sourceDay.fleet_time_utilization_pct);
  const fleetTimeUtilizationPct = fleetTimeUtilizationPctValue === null
    ? (fleetMaxWorkMinutes > 0 && fleetWorkedMinutes !== null
      ? (fleetWorkedMinutes / fleetMaxWorkMinutes) * 100
      : null)
    : Math.max(fleetTimeUtilizationPctValue, 0);
  const fleetUnproductiveMinutesValue = toOptionalFiniteNumber(sourceDay.fleet_unproductive_minutes);
  const fleetUnproductiveMinutes = fleetUnproductiveMinutesValue === null
    ? (fleetMaxWorkMinutes !== null && fleetWorkedMinutes !== null
      ? Math.max(fleetMaxWorkMinutes - fleetWorkedMinutes, 0)
      : null)
    : Math.max(fleetUnproductiveMinutesValue, 0);
  const fleetUnproductivePctValue = toOptionalFiniteNumber(sourceDay.fleet_unproductive_pct);
  const fleetUnproductivePct = fleetUnproductivePctValue === null
    ? (fleetMaxWorkMinutes > 0 && fleetUnproductiveMinutes !== null
      ? (fleetUnproductiveMinutes / fleetMaxWorkMinutes) * 100
      : null)
    : Math.max(fleetUnproductivePctValue, 0);
  const deliveryVisitCount = Math.max(toOptionalFiniteNumber(sourceDay.delivery_visit_count) ?? 0, 0);
  const lowLoadDistanceKmValue = toOptionalFiniteNumber(sourceDay.low_load_distance_km);
  const classifiedDistanceKmValue = toOptionalFiniteNumber(sourceDay.classified_distance_km);
  const lowLoadDistanceKm = lowLoadDistanceKmValue === null ? null : Math.max(lowLoadDistanceKmValue, 0);
  const classifiedDistanceKm = classifiedDistanceKmValue === null ? null : Math.max(classifiedDistanceKmValue, 0);
  const lowLoadDistancePctValue = toOptionalFiniteNumber(sourceDay.low_load_distance_pct);
  const lowLoadDistancePct = lowLoadDistancePctValue === null
    ? (classifiedDistanceKm > 0 && lowLoadDistanceKm !== null
      ? (lowLoadDistanceKm / classifiedDistanceKm) * 100
      : null)
    : Math.max(lowLoadDistancePctValue, 0);
  const lowLoadThresholdPct = Math.max(
    toOptionalFiniteNumber(sourceDay.low_load_threshold_pct) ?? DEFAULT_LOW_LOAD_THRESHOLD_PCT,
    0,
  );
  const fleetUsagePct = fleetTruckCount > 0
    ? Math.min((activeTruckCount / fleetTruckCount) * 100, 100)
    : Math.max(toOptionalNumber(sourceDay.used_fleet_pct) ?? 0, 0);
  const truckOccupancyPct = fleetTruckCount > 0
    ? Math.min((usedTruckCount / fleetTruckCount) * 100, 100)
    : Math.max(toOptionalNumber(sourceDay.avg_time_budget_consumed_pct) ?? 0, 0);
  const usedFleetPct = activeTruckCount > 0
    ? Math.min((usedTruckCount / activeTruckCount) * 100, 100)
    : Math.max(toOptionalNumber(sourceDay.used_fleet_pct) ?? 0, 0);
  const dispatchOrderCount = Math.max(toNumber(sourceDay.dispatch_order_count, 0), 0);
  const hasSnapshot = sourceDay.has_snapshot === true;
  const hasDispatch = sourceDay.has_dispatch === true || dispatchOrderCount > 0 || hasSnapshot;
  const gaugeRatio = avgMaxWorkMinutes && avgMaxWorkMinutes > 0
    ? Math.min(avgAssignedDurationMinutes / avgMaxWorkMinutes, 1)
    : Math.min(avgTimeBudgetConsumedPct / 100, 1);

  return {
    activeTruckCount,
    avgAssignedDurationLabel: formatDurationCompact(avgAssignedDurationMinutes),
    fleetUtilization: normalizeFleetUtilization(sourceDay.fleet_utilization),
    avgAssignedDurationMinutes,
    avgBusinessStopCountPerUsedTruck,
    avgBusinessStopCountPerUsedTruckLabel: formatDecimal(avgBusinessStopCountPerUsedTruck, 1) || "0",
    avgCompartmentFillLabel: avgCompartmentFillPct === null ? "n/d" : formatPercentLabel(avgCompartmentFillPct),
    avgCompartmentFillPct,
    avgCompartmentUsageLabel: avgCompartmentUsagePct === null ? "n/d" : formatPercentLabel(avgCompartmentUsagePct),
    avgCompartmentUsagePct,
    avgCompartmentsUsed,
    avgCompartmentsUsedLabel: avgCompartmentsUsed === null ? "n/d" : (formatDecimal(avgCompartmentsUsed, 1) || "0"),
    avgDistancePerUsedTruckKm,
    avgDistancePerUsedTruckLabel: formatDistanceCompact(avgDistancePerUsedTruckKm),
    avgHighLoadDriveShareLabel: formatPercentLabel(avgHighLoadDriveSharePct),
    avgHighLoadDriveSharePct,
    avgMaxWorkLabel: avgMaxWorkMinutes && avgMaxWorkMinutes > 0 ? formatDurationCompact(avgMaxWorkMinutes) : null,
    avgMaxWorkMinutes,
    avgPeakCompartmentOccupancyLabel: formatPercentLabel(avgPeakCompartmentOccupancyPct),
    avgPeakCompartmentOccupancyPct,
    avgPeakCompartmentsUsed,
    avgPeakCompartmentsUsedLabel: formatDecimal(avgPeakCompartmentsUsed, 1) || "0",
    avgPeakLoadFillRateLabel: avgPeakLoadFillRatePct === null ? "n/d" : formatPercentLabel(avgPeakLoadFillRatePct),
    avgPeakLoadFillRatePct,
    avgTonsPerDeliveryStopLabel: `${formatTonnage(avgTonsPerDeliveryStopTons)} t`,
    avgTonsPerDeliveryStopTons,
    avgTimeBudgetConsumedLabel: formatPercentLabel(avgTimeBudgetConsumedPct),
    avgTimeBudgetConsumedPct,
    avgTonnagePerUsedTruckLabel: `${formatTonnage(avgTonnagePerUsedTruckTons)} t`,
    avgTonnagePerUsedTruckTons,
    compartmentFillRateLabel: formatPercentLabel(compartmentFillRatePct),
    compartmentFillRatePct,
    compartmentNote: totalCompartments > 0
      ? `${avgCompartmentsUsed === null ? usedCompartments : formatDecimal(avgCompartmentsUsed, 1) || "0"} / ${totalCompartments} compartiments utilisés en moyenne`
      : "Aucun compartiment actif",
    dateLabel: resolveDateLabel(parsedDispatchDate),
    dayLabel: resolveWeekdayLabel(parsedDispatchDate, sourceDay.day_label),
    deliveryVisitCount,
    deliveryVisitCountLabel: formatDecimal(deliveryVisitCount, 1) || "0",
    dispatchEstimatedCostCad,
    dispatchEstimatedCostLabel: formatCurrencyCompact(dispatchEstimatedCostCad),
    realCostCad: toOptionalFiniteNumber(sourceDay.real_cost_cad),
    realCostDistanceCad: toOptionalFiniteNumber(sourceDay.real_cost_distance_cad),
    realCostTruckTimeCad: toOptionalFiniteNumber(sourceDay.real_cost_truck_time_cad),
    realCostDriverCad: toOptionalFiniteNumber(sourceDay.real_cost_driver_cad),
    missionMinutes: toOptionalFiniteNumber(sourceDay.mission_minutes),
    realCostMissingTruckCount: Math.max(toNumber(sourceDay.real_cost_missing_truck_count, 0), 0),
    plannedRevenueCad: toOptionalFiniteNumber(sourceDay.planned_revenue_cad),
    plannedRevenueHourlyCad: toOptionalFiniteNumber(sourceDay.planned_revenue_hourly_cad),
    plannedRevenueSupplementsCad: toOptionalFiniteNumber(sourceDay.planned_revenue_supplements_cad),
    plannedRevenueFixedCad: toOptionalFiniteNumber(sourceDay.planned_revenue_fixed_cad),
    revenueCoveragePct: toOptionalFiniteNumber(sourceDay.revenue_coverage_pct),
    ordersWithoutRate: Math.max(toNumber(sourceDay.orders_without_rate, 0), 0),
    dispatchOrderCount,
    dispatchProductCount,
    preloadTruckCount,
    preloadTruckLabel: formatPercentLabel(preloadTruckPct),
    preloadTruckPct,
    priorityWindowTruckCount,
    priorityWindowTruckLabel: formatPercentLabel(priorityWindowTruckPct),
    priorityWindowTruckPct,
    protectedOrderCount,
    protectedOrderLabel: formatPercentLabel(protectedOrderPct),
    protectedOrderPct,
    protectedOrderTotalCount,
    dispatchDate,
    fleetWorkedMinutes,
    fleetWorkedLabel: fleetWorkedMinutes === null ? "n/d" : formatDurationCompact(fleetWorkedMinutes),
    fleetMaxWorkMinutes,
    fleetMaxWorkLabel: fleetMaxWorkMinutes === null ? "n/d" : formatDurationCompact(fleetMaxWorkMinutes),
    fleetTimeUtilizationPct,
    fleetTimeUtilizationLabel: fleetTimeUtilizationPct === null ? "n/d" : formatPercentLabel(fleetTimeUtilizationPct),
    fleetUnproductiveMinutes,
    fleetUnproductiveLabel: fleetUnproductivePct === null ? "n/d" : formatPercentLabel(fleetUnproductivePct),
    fleetUnproductivePct,
    gaugeRatio,
    hasDispatch,
    hasSnapshot,
    fleetTruckCount,
    fleetUsagePct,
    plannedTonnageLabel: `${formatTonnage(plannedTonnageTons)} t`,
    plannedTonnageTons,
    totalCompartments,
    usedCompartments,
    usedFleetLabel: formatPercentLabel(usedFleetPct),
    usedFleetNote: activeTruckCount > 0
      ? `${usedTruckCount} / ${activeTruckCount} camions actifs mobilisés`
      : "Aucun camion actif",
    usedFleetPct,
    usedTruckCount,
    truckOccupancyPct,
    lowLoadDistanceKm,
    lowLoadDistanceLabel: lowLoadDistanceKm === null ? "n/d" : formatDistanceCompact(lowLoadDistanceKm),
    classifiedDistanceKm,
    classifiedDistanceLabel: classifiedDistanceKm === null ? "n/d" : formatDistanceCompact(classifiedDistanceKm),
    lowLoadDistancePct,
    lowLoadDistancePctLabel: lowLoadDistancePct === null ? "n/d" : formatPercentLabel(lowLoadDistancePct),
    lowLoadThresholdPct,
    ...normalizeDashboardLowLoad(sourceDay),
  };
}

function averageAnalyticsMetric(items = [], accessor, { positiveOnly = false } = {}) {
  const values = items
    .map((item) => Number(accessor(item)))
    .filter((value) => Number.isFinite(value) && (!positiveOnly || value > 0));

  if (!values.length) {
    return null;
  }

  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function sumAnalyticsMetric(items = [], accessor) {
  return items.reduce((sum, item) => sum + Math.max(Number(accessor(item)) || 0, 0), 0);
}

function sumOptionalAnalyticsMetric(items = [], accessor) {
  const values = items
    .map((item) => accessor(item))
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  return values.length ? values.reduce((sum, value) => sum + Math.max(value, 0), 0) : null;
}

function hasFiniteOptionalAnalyticsValue(value) {
  return value !== null
    && value !== undefined
    && value !== ""
    && Number.isFinite(Number(value));
}

function hasAnalyticsMetricData(day = null) {
  if (!day) {
    return false;
  }

  if (day.hasSnapshot === true) {
    return true;
  }

  return [
    day.activeTruckCount,
    day.avgAssignedDurationMinutes,
    day.avgBusinessStopCountPerUsedTruck,
    day.avgCompartmentsUsed,
    day.avgCompartmentUsagePct,
    day.avgCompartmentFillPct,
    day.avgDistancePerUsedTruckKm,
    day.avgHighLoadDriveSharePct,
    day.avgMaxWorkMinutes,
    day.avgPeakCompartmentOccupancyPct,
    day.avgPeakCompartmentsUsed,
    day.avgPeakLoadFillRatePct,
    day.avgTonsPerDeliveryStopTons,
    day.avgTimeBudgetConsumedPct,
    day.avgTonnagePerUsedTruckTons,
    day.fleetWorkedMinutes,
    day.fleetMaxWorkMinutes,
    day.fleetTimeUtilizationPct,
    day.fleetUnproductiveMinutes,
    day.fleetUnproductivePct,
    day.deliveryVisitCount,
    day.lowLoadDistanceKm,
    day.classifiedDistanceKm,
    day.lowLoadDistancePct,
    day.fleetUsagePct,
    day.usedFleetPct,
    day.truckOccupancyPct,
    day.plannedTonnageTons,
    day.dispatchEstimatedCostCad,
    day.dispatchProductCount,
    day.preloadTruckPct,
    day.priorityWindowTruckPct,
    day.protectedOrderPct,
    day.usedCompartments,
    day.totalCompartments,
    day.compartmentFillRatePct,
  ].some((value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0;
  });
}

function normalizeSummaryDay(day) {
  if (!isPlainObject(day)) {
    return day;
  }

  const hasApiAnalyticsField = [
    "dispatch_date",
    "has_dispatch",
    "has_snapshot",
    "fleet_worked_minutes",
    "fleet_max_work_minutes",
    "fleet_time_utilization_pct",
    "fleet_unproductive_minutes",
    "fleet_unproductive_pct",
    "delivery_visit_count",
    "low_load_distance_km",
    "classified_distance_km",
    "low_load_distance_pct",
    "low_load_threshold_pct",
  ].some((key) => Object.prototype.hasOwnProperty.call(day, key));

  return hasApiAnalyticsField ? resolveAnalyticsDay(day) : day;
}

export function summarizeAnalyticsPeriodDays(days = [], { fallbackDay = null } = {}) {
  const resolvedDays = Array.isArray(days) ? days.filter(Boolean).map(normalizeSummaryDay) : [];
  const resolvedFallbackDay = normalizeSummaryDay(fallbackDay);
  const dispatchDays = resolvedDays.filter((day) => day?.hasDispatch);
  const effectiveDispatchDays = dispatchDays.length
    ? dispatchDays
    : resolvedFallbackDay?.hasDispatch
      ? [resolvedFallbackDay]
      : [];
  const sampleDays = effectiveDispatchDays.filter(hasAnalyticsMetricData);
  const totalDaysCount = resolvedDays.length || (resolvedFallbackDay ? 1 : 0);
  const fleetUtilizationFields = fleetUtilizationReportFields(effectiveDispatchDays);

  if (!sampleDays.length) {
    return {
      ...fleetUtilizationFields,
      activeTruckCount: 0,
      avgAssignedDurationMinutes: null,
      avgBusinessStopCountPerUsedTruck: null,
      avgCompartmentFillPct: null,
      avgCompartmentUsagePct: null,
      avgCompartmentsUsed: null,
      avgDispatchOrderCount: null,
      avgDistancePerUsedTruckKm: null,
      avgHighLoadDriveSharePct: null,
      avgMaxWorkMinutes: null,
      avgPeakCompartmentOccupancyPct: null,
      avgPeakCompartmentsUsed: null,
      avgPeakLoadFillRatePct: null,
      avgPlannedTonnageTons: null,
      avgTonsPerDeliveryStopTons: null,
      avgTimeBudgetConsumedPct: null,
      avgTonnagePerUsedTruckTons: null,
      avgFleetUsagePct: null,
      availableCompartments: null,
      compartmentFillRatio: null,
      dispatchEstimatedCostCad: null,
      realCostDayCount: 0,
      realCostMissingTruckCount: 0,
      realCostPerDispatchCad: null,
      realCostPerTonneCad: null,
      realCostDistancePerTonneCad: null,
      realCostTruckTimePerTonneCad: null,
      realCostDriverPerTonneCad: null,
      realCostPerOrderCad: null,
      realCostPerHourCad: null,
      dispatchDayCount: effectiveDispatchDays.length,
      dispatchProductCount: null,
      preloadTruckCount: 0,
      preloadTruckPct: null,
      priorityWindowTruckCount: 0,
      priorityWindowTruckPct: null,
      protectedOrderCount: 0,
      protectedOrderPct: null,
      protectedOrderTotalCount: 0,
      hasData: false,
      sampleCount: 0,
      totalCompartments: 0,
      totalDaysCount,
      totalPlannedTonnageTons: null,
      usedCompartments: null,
      usedFleetPct: null,
      usedTruckCount: 0,
      avgTruckOccupancyPct: null,
      fleetWorkedMinutes: null,
      fleetMaxWorkMinutes: null,
      fleetTimeUtilizationPct: null,
      fleetUnproductiveMinutes: null,
      fleetUnproductivePct: null,
      fleetValidDayCount: 0,
      deliveryVisitCount: 0,
      avgDeliveryVisitCountPerActiveTruck: null,
      lowLoadDistanceKm: null,
      classifiedDistanceKm: null,
      lowLoadDistancePct: null,
      loadedDistancePct: null,
      lowLoadThresholdPct: DEFAULT_LOW_LOAD_THRESHOLD_PCT,
      lowLoadValidDayCount: 0,
      lowLoadCoveragePct: null,
      totalActiveTruckCount: 0,
      validDayCount: 0,
      validAnalyticsDayCount: 0,
      analyticsCoveragePct: 0,
      coveragePct: 0,
    };
  }

  const avgAssignedDurationMinutes = averageAnalyticsMetric(sampleDays, (day) => day?.avgAssignedDurationMinutes) ?? 0;
  const avgBusinessStopCountPerUsedTruck = averageAnalyticsMetric(sampleDays, (day) => day?.avgBusinessStopCountPerUsedTruck);
  const avgCompartmentsUsed = averageAnalyticsMetric(sampleDays, (day) => day?.avgCompartmentsUsed);
  const avgCompartmentUsagePct = averageAnalyticsMetric(sampleDays, (day) => day?.avgCompartmentUsagePct);
  const avgCompartmentFillPct = averageAnalyticsMetric(sampleDays, (day) => day?.avgCompartmentFillPct);
  const avgDispatchOrderCount = averageAnalyticsMetric(sampleDays, (day) => day?.dispatchOrderCount);
  const avgDistancePerUsedTruckKm = averageAnalyticsMetric(sampleDays, (day) => day?.avgDistancePerUsedTruckKm);
  const avgHighLoadDriveSharePct = averageAnalyticsMetric(sampleDays, (day) => day?.avgHighLoadDriveSharePct);
  const avgMaxWorkMinutes = averageAnalyticsMetric(sampleDays, (day) => day?.avgMaxWorkMinutes, { positiveOnly: true });
  const avgPeakCompartmentOccupancyPct = averageAnalyticsMetric(sampleDays, (day) => day?.avgPeakCompartmentOccupancyPct);
  const avgPeakCompartmentsUsed = averageAnalyticsMetric(sampleDays, (day) => day?.avgPeakCompartmentsUsed);
  const peakLoadDays = sampleDays.filter((day) => hasFiniteOptionalAnalyticsValue(day?.avgPeakLoadFillRatePct));
  const avgPeakLoadFillRatePct = averageAnalyticsMetric(peakLoadDays, (day) => day.avgPeakLoadFillRatePct);
  const avgTonsPerDeliveryStopTons = averageAnalyticsMetric(sampleDays, (day) => day?.avgTonsPerDeliveryStopTons);
  const avgTimeBudgetConsumedPct = averageAnalyticsMetric(sampleDays, (day) => day?.avgTimeBudgetConsumedPct);
  const avgTonnagePerUsedTruckTons = averageAnalyticsMetric(sampleDays, (day) => day?.avgTonnagePerUsedTruckTons);
  const avgFleetUsagePct = averageAnalyticsMetric(sampleDays, (day) => day?.fleetUsagePct);
  const activeTruckCount = averageAnalyticsMetric(sampleDays, (day) => day?.activeTruckCount) ?? 0;
  const totalActiveTruckCount = sumAnalyticsMetric(sampleDays, (day) => day?.activeTruckCount);
  const dispatchEstimatedCostCad = averageAnalyticsMetric(sampleDays, (day) => day?.dispatchEstimatedCostCad);
  // Real cost: only the days whose cost is known, and every ratio uses those same days.
  const realCostDays = sampleDays.filter((day) => hasFiniteOptionalAnalyticsValue(day?.realCostCad));
  const realCostTotal = sumAnalyticsMetric(realCostDays, (day) => day.realCostCad);
  const realCostTonnage = sumAnalyticsMetric(realCostDays, (day) => day.plannedTonnageTons);
  const realCostOrders = sumAnalyticsMetric(realCostDays, (day) => day.dispatchOrderCount);
  const realCostHours = sumAnalyticsMetric(realCostDays, (day) => day.missionMinutes) / 60;
  const perRealCostTonne = (total) => (realCostDays.length && realCostTonnage > 0 ? total / realCostTonnage : null);
  const realCostFields = {
    realCostDayCount: realCostDays.length,
    realCostMissingTruckCount: sumAnalyticsMetric(sampleDays, (day) => day?.realCostMissingTruckCount),
    realCostPerDispatchCad: realCostDays.length ? realCostTotal / realCostDays.length : null,
    realCostPerTonneCad: perRealCostTonne(realCostTotal),
    realCostDistancePerTonneCad: perRealCostTonne(sumAnalyticsMetric(realCostDays, (day) => day.realCostDistanceCad)),
    realCostTruckTimePerTonneCad: perRealCostTonne(sumAnalyticsMetric(realCostDays, (day) => day.realCostTruckTimeCad)),
    realCostDriverPerTonneCad: perRealCostTonne(sumAnalyticsMetric(realCostDays, (day) => day.realCostDriverCad)),
    realCostPerOrderCad: realCostOrders > 0 ? realCostTotal / realCostOrders : null,
    realCostPerHourCad: realCostHours > 0 ? realCostTotal / realCostHours : null,
  };
  const dispatchProductCount = averageAnalyticsMetric(sampleDays, (day) => day?.dispatchProductCount);
  const preloadTruckCount = averageAnalyticsMetric(sampleDays, (day) => day?.preloadTruckCount) ?? 0;
  const preloadTruckPct = averageAnalyticsMetric(sampleDays, (day) => day?.preloadTruckPct);
  const priorityWindowTruckCount = averageAnalyticsMetric(sampleDays, (day) => day?.priorityWindowTruckCount) ?? 0;
  const priorityWindowTruckPct = averageAnalyticsMetric(sampleDays, (day) => day?.priorityWindowTruckPct);
  const protectedOrderCount = averageAnalyticsMetric(sampleDays, (day) => day?.protectedOrderCount) ?? 0;
  const protectedOrderPct = averageAnalyticsMetric(sampleDays, (day) => day?.protectedOrderPct);
  const protectedOrderTotalCount = averageAnalyticsMetric(sampleDays, (day) => day?.protectedOrderTotalCount) ?? 0;
  const usedTruckCount = averageAnalyticsMetric(sampleDays, (day) => day?.usedTruckCount) ?? 0;
  const totalUsedTruckCount = sumAnalyticsMetric(sampleDays, (day) => day?.usedTruckCount);
  const avgTruckOccupancyPct = averageAnalyticsMetric(sampleDays, (day) => day?.truckOccupancyPct);
  const fleetValidDays = sampleDays.filter((day) => (
    hasFiniteOptionalAnalyticsValue(day?.fleetWorkedMinutes)
    && hasFiniteOptionalAnalyticsValue(day?.fleetMaxWorkMinutes)
    && Number(day.fleetMaxWorkMinutes) > 0
  ));
  const fleetWorkedMinutes = sumOptionalAnalyticsMetric(fleetValidDays, (day) => day?.fleetWorkedMinutes);
  const fleetMaxWorkMinutes = sumOptionalAnalyticsMetric(fleetValidDays, (day) => day?.fleetMaxWorkMinutes);
  const fleetValidDayCount = fleetValidDays.length;
  const fleetUnproductiveMinutes = fleetMaxWorkMinutes !== null && fleetWorkedMinutes !== null
    ? Math.max(fleetMaxWorkMinutes - fleetWorkedMinutes, 0)
    : null;
  const fleetTimeUtilizationPct = fleetMaxWorkMinutes !== null && fleetMaxWorkMinutes > 0 && fleetWorkedMinutes !== null
    ? (fleetWorkedMinutes / fleetMaxWorkMinutes) * 100
    : null;
  const fleetUnproductivePct = fleetMaxWorkMinutes !== null && fleetMaxWorkMinutes > 0 && fleetUnproductiveMinutes !== null
    ? (fleetUnproductiveMinutes / fleetMaxWorkMinutes) * 100
    : null;
  const deliveryVisitCount = sumAnalyticsMetric(sampleDays, (day) => day?.deliveryVisitCount);
  const avgDeliveryVisitCountPerActiveTruck = totalActiveTruckCount > 0
    ? deliveryVisitCount / totalActiveTruckCount
    : null;
  const lowLoadValidDays = sampleDays.filter((day) => (
    hasFiniteOptionalAnalyticsValue(day?.classifiedDistanceKm)
    && Number(day.classifiedDistanceKm) > 0
    && hasFiniteOptionalAnalyticsValue(day?.lowLoadDistanceKm)
  ));
  const lowLoadDistanceKm = sumOptionalAnalyticsMetric(lowLoadValidDays, (day) => day?.lowLoadDistanceKm);
  const classifiedDistanceKm = sumOptionalAnalyticsMetric(lowLoadValidDays, (day) => day?.classifiedDistanceKm);
  const lowLoadValidDayCount = lowLoadValidDays.length;
  const lowLoadDistancePct = classifiedDistanceKm > 0 && lowLoadDistanceKm !== null
    ? (lowLoadDistanceKm / classifiedDistanceKm) * 100
    : null;
  const lowLoadThresholdPct = sampleDays.find((day) => hasFiniteOptionalAnalyticsValue(day?.lowLoadThresholdPct))?.lowLoadThresholdPct
    ?? DEFAULT_LOW_LOAD_THRESHOLD_PCT;
  const lowLoadCoveragePct = effectiveDispatchDays.length > 0
    ? (lowLoadValidDayCount / effectiveDispatchDays.length) * 100
    : null;
  const validDayCount = sampleDays.length;
  const analyticsCoveragePct = effectiveDispatchDays.length > 0
    ? (validDayCount / effectiveDispatchDays.length) * 100
    : 0;
  const coveragePct = totalDaysCount > 0
    ? (validDayCount / totalDaysCount) * 100
    : 0;
  const usedFleetPct = totalActiveTruckCount > 0
    ? Math.min((totalUsedTruckCount / totalActiveTruckCount) * 100, 100)
    : averageAnalyticsMetric(sampleDays, (day) => day?.usedFleetPct);
  const totalCompartments = sampleDays.reduce(
    (maximum, day) => Math.max(maximum, Math.max(toNumber(day?.totalCompartments, 0), 0)),
    0,
  );
  const usedCompartments = averageAnalyticsMetric(sampleDays, (day) => day?.usedCompartments) ?? 0;
  const compartmentFillRatio = totalCompartments > 0
    ? Math.min(Math.max(usedCompartments / totalCompartments, 0), 1)
    : Math.min(Math.max((averageAnalyticsMetric(sampleDays, (day) => day?.compartmentFillRatePct) ?? 0) / 100, 0), 1);
  const totalPlannedTonnageTons = sumAnalyticsMetric(sampleDays, (day) => day?.plannedTonnageTons);
  const avgPlannedTonnageTons = averageAnalyticsMetric(sampleDays, (day) => day?.plannedTonnageTons) ?? 0;
  const availableCompartments = Math.max(totalCompartments - usedCompartments, 0);

  return {
    activeTruckCount,
    avgAssignedDurationMinutes,
    ...fleetUtilizationFields,
    avgBusinessStopCountPerUsedTruck,
    avgCompartmentFillPct,
    avgCompartmentUsagePct,
    avgCompartmentsUsed,
    avgDispatchOrderCount,
    avgDistancePerUsedTruckKm,
    avgHighLoadDriveSharePct,
    avgMaxWorkMinutes,
    avgPeakCompartmentOccupancyPct,
    avgPeakCompartmentsUsed,
    avgPeakLoadFillRatePct,
    avgPlannedTonnageTons,
    avgTonsPerDeliveryStopTons,
    avgTimeBudgetConsumedPct,
    avgTonnagePerUsedTruckTons,
    avgFleetUsagePct,
    availableCompartments,
    compartmentFillRatio,
    dispatchEstimatedCostCad,
    ...realCostFields,
    dispatchDayCount: effectiveDispatchDays.length,
    dispatchProductCount,
    preloadTruckCount,
    preloadTruckPct,
    priorityWindowTruckCount,
    priorityWindowTruckPct,
    protectedOrderCount,
    protectedOrderPct,
    protectedOrderTotalCount,
    hasData: true,
    sampleCount: sampleDays.length,
    totalCompartments,
    totalDaysCount,
    totalPlannedTonnageTons,
    usedCompartments,
    usedFleetPct,
    usedTruckCount,
    avgTruckOccupancyPct,
    fleetWorkedMinutes,
    fleetMaxWorkMinutes,
    fleetTimeUtilizationPct,
    fleetUnproductiveMinutes,
    fleetUnproductivePct,
    fleetValidDayCount,
    deliveryVisitCount,
    avgDeliveryVisitCountPerActiveTruck,
    lowLoadDistanceKm,
    classifiedDistanceKm,
    lowLoadDistancePct,
    loadedDistancePct: lowLoadDistancePct === null ? null : 100 - lowLoadDistancePct,
    lowLoadThresholdPct,
    lowLoadValidDayCount,
    lowLoadCoveragePct,
    totalActiveTruckCount,
    validDayCount,
    validAnalyticsDayCount: validDayCount,
    analyticsCoveragePct,
    coveragePct,
  };
}
function formatOptimizationTimestamp(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return {
      date: value,
      time: null,
    };
  }

  return {
    date: DATE_FORMATTER.format(parsedDate),
    dayMonth: DAY_MONTH_FORMATTER.format(parsedDate),
    time: TIME_FORMATTER.format(parsedDate),
  };
}

function normalizeTruckStatus(status, picCharge, isActive) {
  if (status === "assigned" || status === "available" || status === "unassigned") {
    return status;
  }

  if (picCharge > 0) {
    return "assigned";
  }

  if (isActive === false) {
    return "unassigned";
  }

  return "available";
}

function normalizeTruckList(source) {
  const candidateList = isPlainObject(source) && Array.isArray(source.list)
    ? source.list
    : Array.isArray(source)
      ? source
      : [];

  return candidateList
    .map((truck, index) => {
      if (!isPlainObject(truck)) {
        return null;
      }

      const capacityMax = Math.max(
        toNumber(truck.capacity_max, toNumber(truck.capacity_tons, 0)),
        0,
      );
      const picCharge = Math.max(
        toNumber(truck.pic_charge, toNumber(truck.current_load_tons, 0)),
        0,
      );
      const name = normalizeText(truck.name)
        || normalizeText(truck.truck_name)
        || normalizeText(truck.truck_id)
        || `Camion ${index + 1}`;
      const content = normalizeText(truck.content) || normalizeText(truck.grain_type) || null;

      return {
        capacity_max: capacityMax,
        content,
        name,
        pic_charge: picCharge,
        status: normalizeTruckStatus(truck.status, picCharge, truck.is_active),
      };
    })
    .filter(Boolean);
}

function resolveOrdersData(rawDashboard, missionsCount) {
  const rawOrders = rawDashboard.orders;
  const rawOrderList = Array.isArray(rawDashboard.raw_orders) ? rawDashboard.raw_orders : [];

  if (isPlainObject(rawOrders)) {
    const total = toNumber(rawOrders.total, rawOrderList.length || missionsCount || EMPTY_DASHBOARD.orders.total);
    const assigned = toNumber(rawOrders.assigned, Math.min(total, missionsCount));
    const pending = toNumber(rawOrders.pending, Math.max(total - assigned, 0));

    return {
      assigned,
      pending,
      total,
    };
  }

  if (rawOrderList.length) {
    const total = rawOrderList.length;
    const assigned = Math.min(total, missionsCount);

    return {
      assigned,
      pending: Math.max(total - assigned, 0),
      total,
    };
  }

  return { ...EMPTY_DASHBOARD.orders };
}

function resolveAnalyticsHistoryState(days = []) {
  const dispatchCount = days.filter((day) => day.hasDispatch).length;
  const snapshotCount = days.filter((day) => day.hasSnapshot).length;

  if (dispatchCount === 0) {
    return {
      hasHistory: false,
      historyState: "empty",
      dispatchCount,
      snapshotCount,
    };
  }

  return {
    hasHistory: true,
    historyState: dispatchCount === days.length ? "ready" : "partial",
    dispatchCount,
    snapshotCount,
  };
}

function buildAnalyticsPeriod({ days = [], key, label, shortLabel }) {
  const resolvedDays = Array.isArray(days) ? days : [];
  const history = resolveAnalyticsHistoryState(resolvedDays);

  return {
    availableDispatchCount: history.dispatchCount,
    availableSnapshotCount: history.snapshotCount,
    days: resolvedDays,
    hasHistory: history.hasHistory,
    historyState: history.historyState,
    key,
    label,
    shortLabel,
  };
}

function resolveAnalyticsData(readModel, fleetTruckCount = 0) {
  const rawAnalytics = isPlainObject(readModel.analytics) ? readModel.analytics : {};
  const rawHistoryWeek = Array.isArray(rawAnalytics.last_7_days) ? rawAnalytics.last_7_days : [];
  const rawHistoryMonth = Array.isArray(rawAnalytics.last_30_days) ? rawAnalytics.last_30_days : [];
  const current = resolveAnalyticsDay(rawAnalytics.current, null, fleetTruckCount);
  const currentDays = current.hasDispatch || current.hasSnapshot ? [current] : [];
  const last30Days = rawHistoryMonth.length
    ? rawHistoryMonth.map((day, index) => resolveAnalyticsDay(day, current.dispatchDate || String(index), fleetTruckCount))
    : rawHistoryWeek.length
      ? rawHistoryWeek.map((day, index) => resolveAnalyticsDay(day, current.dispatchDate || String(index), fleetTruckCount))
      : current.hasDispatch || current.hasSnapshot
        ? [current]
        : [];
  const last7Days = rawHistoryWeek.length
    ? rawHistoryWeek.map((day, index) => resolveAnalyticsDay(day, current.dispatchDate || String(index), fleetTruckCount))
    : last30Days.length
      ? last30Days.slice(-7)
      : current.hasDispatch || current.hasSnapshot
        ? [current]
        : [];
  const previous30Days = Array.isArray(rawAnalytics.previous_30_days)
    ? rawAnalytics.previous_30_days.map((day, index) => resolveAnalyticsDay(day, String(index), fleetTruckCount))
    : [];
  const yesterday = rawAnalytics.yesterday ? resolveAnalyticsDay(rawAnalytics.yesterday, null, 0) : null;
  // « Hier » is compared with the weekday before it (Friday for a Monday); the comparison is hidden when that day had no dispatch.
  const yesterdayTime = yesterday?.dispatchDate ? Date.parse(`${yesterday.dispatchDate.slice(0, 10)}T12:00:00Z`) : null;
  const daysBack = yesterdayTime === null ? 0 : new Date(yesterdayTime).getUTCDay() === 1 ? 3 : 1;
  const dayBeforeYesterday = yesterdayTime === null ? null : new Date(yesterdayTime - daysBack * 86400000).toISOString().slice(0, 10);
  const veille = [
    ...(rawAnalytics.day_before_yesterday ? [resolveAnalyticsDay(rawAnalytics.day_before_yesterday, null, 0)] : []),
    ...previous30Days,
    ...last30Days,
  ].find((day) => day.dispatchDate?.slice(0, 10) === dayBeforeYesterday && day.hasDispatch);
  const previousPeriod = (days, key) => (days.length ? buildAnalyticsPeriod({ days, key, label: "Période précédente", shortLabel: "Précédente" }) : null);
  // The home reads the last complete Monday-to-Friday week and the week before it, weekends left out; « 30 dispatchs » has its own request.
  const weekBounds = normalizeText(rawAnalytics.week_start) && normalizeText(rawAnalytics.week_end);
  const daysBetween = (start, end) => last30Days.filter((day) => day.dispatchDate?.slice(0, 10) >= start && day.dispatchDate?.slice(0, 10) <= end);
  const dayMonth = (value) => `${value.slice(8, 10)}/${value.slice(5, 7)}`;
  const weekDays = weekBounds ? daysBetween(rawAnalytics.week_start, rawAnalytics.week_end) : last7Days;
  const previousWeekDays = weekBounds ? daysBetween(rawAnalytics.previous_week_start || "", rawAnalytics.previous_week_end || "") : last30Days.slice(-14, -7);
  const periods = {
    day: {
      ...buildAnalyticsPeriod({
        days: yesterday ? [yesterday] : [],
        key: "day",
        label: "Hier",
        shortLabel: "Hier",
      }),
      previous: previousPeriod(veille ? [veille] : [], "day"),
    },
    month: {
      ...buildAnalyticsPeriod({
        days: last30Days,
        key: "month",
        label: "30 dispatchs actifs",
        shortLabel: "30 dispatchs",
      }),
      previous: previousPeriod(previous30Days, "month"),
    },
    week: {
      ...buildAnalyticsPeriod({
        days: weekDays,
        key: "week",
        label: weekBounds ? `Semaine du ${dayMonth(rawAnalytics.week_start)} au ${dayMonth(rawAnalytics.week_end)}` : "7 dispatchs actifs",
        shortLabel: weekBounds ? "Semaine" : "7 dispatchs",
      }),
      previous: previousPeriod(previousWeekDays, "week"),
    },
  };
  const periodOptions = [
    { key: "day", label: "Hier" },
    { key: "week", label: weekBounds ? "Semaine" : "7 dispatchs" },
    { key: "month", label: "30 dispatchs" },
  ];
  const defaultPeriodKey = rawAnalytics.history_period === "week"
    ? "week"
    : periods.month.hasHistory
      ? "month"
      : periods.week.hasHistory
        ? "week"
        : "month";
  // « Hier » and its day before may lie outside the loaded weeks: the day view adds them to its context.
  const dayContextDays = [...new Map([...last30Days, veille, yesterday].filter((day) => day?.dispatchDate)
    .map((day) => [day.dispatchDate.slice(0, 10), day])).values()].sort((left, right) => left.dispatchDate.localeCompare(right.dispatchDate));

  return {
    current,
    dayContextDays,
    defaultPeriodKey,
    hasHistory: periods.month.hasHistory || periods.week.hasHistory,
    historyState: periods[defaultPeriodKey].historyState,
    last30Days,
    last7Days,
    monthLoaded: rawAnalytics.history_period !== "week",
    periodOptions,
    periods,
    weekEnd: normalizeText(rawAnalytics.week_end) || null,
  };
}

function resolveTrucksData(rawDashboard) {
  const rawTrucks = rawDashboard.trucks;
  const rawTruckList = Array.isArray(rawDashboard.raw_trucks) ? rawDashboard.raw_trucks : [];
  const listSource = isPlainObject(rawTrucks) && Array.isArray(rawTrucks.list) && rawTrucks.list.length
    ? rawTrucks
    : Array.isArray(rawTrucks) && rawTrucks.length
      ? rawTrucks
      : rawTruckList;
  const list = normalizeTruckList(listSource);

  if (isPlainObject(rawTrucks)) {
    const total = toNumber(rawTrucks.total, list.length);
    const mobilized = toNumber(
      rawTrucks.mobilized,
      list.filter((truck) => truck.status === "assigned").length,
    );
    const active = toNumber(
      rawTrucks.active,
      list.filter((truck) => truck.status !== "unassigned").length,
    );
    const available = toNumber(
      rawTrucks.available,
      Math.max(total - mobilized, 0),
    );

    return {
      active,
      available,
      list,
      mobilized,
      total,
    };
  }

  if (rawTruckList.length || (Array.isArray(rawTrucks) && rawTrucks.length)) {
    const total = list.length;
    const mobilized = list.filter((truck) => truck.status === "assigned").length;
    const active = list.filter((truck) => truck.status !== "unassigned").length;
    const available = list.filter((truck) => truck.status === "available").length;

    return {
      active,
      available,
      list,
      mobilized,
      total,
    };
  }

  return {
    ...EMPTY_DASHBOARD.trucks,
    list: [],
  };
}

function resolveTonnageData(rawDashboard, missions, trucks) {
  const rawTonnage = rawDashboard.tonnage;

  if (isPlainObject(rawTonnage)) {
    return {
      coverage_pct: toNumber(rawTonnage.coverage_pct, EMPTY_DASHBOARD.tonnage.coverage_pct),
      planned: toNumber(rawTonnage.planned, EMPTY_DASHBOARD.tonnage.planned),
    };
  }

  const plannedFromMissions = missions.reduce(
    (sum, mission) => sum + toNumber(mission?.quantity_tons, 0),
    0,
  );
  if (plannedFromMissions > 0) {
    return {
      coverage_pct: 100,
      planned: plannedFromMissions,
    };
  }

  const plannedFromTrucks = trucks.list.reduce(
    (sum, truck) => sum + toNumber(truck.pic_charge, 0),
    0,
  );
  if (plannedFromTrucks > 0) {
    return {
      coverage_pct: 100,
      planned: plannedFromTrucks,
    };
  }

  return { ...EMPTY_DASHBOARD.tonnage };
}

function resolveAlertsData(rawDashboard, dashboardNotice) {
  const rawAlerts = rawDashboard.alerts;

  if (isPlainObject(rawAlerts)) {
    return {
      count: toNumber(rawAlerts.count, 0),
      errors: toNumber(rawAlerts.errors, 0),
      suspended: toNumber(rawAlerts.suspended, 0),
    };
  }

  const suspended = Array.isArray(rawDashboard.alert_messages) ? rawDashboard.alert_messages.length : 0;
  const errors = normalizeText(rawDashboard.error_message) ? 1 : 0;
  const notices = dashboardNotice ? 1 : 0;

  return {
    count: suspended + errors + notices,
    errors,
    suspended: suspended + notices,
  };
}

function resolveLastOptimization(rawDashboard, readModel, missionCount, truckCount) {
  const rawLastOptimization = rawDashboard.last_optimization;

  if (isPlainObject(rawLastOptimization)) {
    return {
      calculated_at: normalizeText(rawLastOptimization.calculated_at) || null,
      missions: toNumber(rawLastOptimization.missions, missionCount),
      status: normalizeText(rawLastOptimization.status)?.toLowerCase() || null,
      trucks: toNumber(rawLastOptimization.trucks, truckCount),
    };
  }

  const latestOptimization = isPlainObject(readModel.latest_optimization)
    ? readModel.latest_optimization
    : null;
  if (latestOptimization?.available && normalizeText(latestOptimization.generated_at)) {
    return {
      calculated_at: latestOptimization.generated_at,
      missions: missionCount,
      status: "ready",
      trucks: truckCount,
    };
  }

  return null;
}

function buildKpis({ alerts, orders, tonnage, trucks }) {
  const orderAccentColor = orders.pending > 0 ? "#c08a2d" : "#3a7d44";
  const orderAccentSurface = orders.pending > 0 ? "rgba(192,138,45,0.14)" : "rgba(58,125,68,0.14)";
  const alertAccentColor = alerts.count > 0 ? "#b42318" : "#3a7d44";
  const alertAccentSurface = alerts.count > 0 ? "rgba(180,35,24,0.12)" : "rgba(58,125,68,0.14)";

  return [
    {
      accentColor: orderAccentColor,
      accentSurface: orderAccentSurface,
      iconKey: "orders",
      key: "orders",
      label: "Commandes",
      note: `${orders.assigned} assignées · ${orders.pending} en attente`,
      tone: orders.pending > 0 ? "warning" : "accent",
      value: `${orders.total} commandes`,
    },
    {
      accentColor: "#516b8c",
      accentSurface: "rgba(81,107,140,0.14)",
      iconKey: "trucks",
      key: "trucks",
      label: "Camions",
      note: `${trucks.mobilized} mobilisés · ${trucks.available} disponibles`,
      tone: "neutral",
      value: `${trucks.active} / ${trucks.total} camions`,
    },
    {
      accentColor: "#64748b",
      accentSurface: "rgba(100,116,139,0.14)",
      iconKey: "tonnage",
      key: "tonnage",
      label: "Tonnage planifié",
      note: `${toNumber(tonnage.coverage_pct, 100)}% du volume visible`,
      tone: "accent",
      value: `${formatTonnage(tonnage.planned)}t planifiés`,
    },
    {
      accentColor: alertAccentColor,
      accentSurface: alertAccentSurface,
      iconKey: "alerts",
      key: "alerts",
      label: "Alertes actives",
      note: `${alerts.suspended} suspendue${alerts.suspended > 1 ? "s" : ""} · ${alerts.errors} erreur${alerts.errors > 1 ? "s" : ""}`,
      tone: alerts.count > 0 ? "danger" : "accent",
      value: `${alerts.count} alerte${alerts.count > 1 ? "s" : ""}`,
    },
  ];
}

export function buildDashboardHomeModel(initialReadModel = null) {
  const readModel = isPlainObject(initialReadModel) ? initialReadModel : {};
  const rawDashboard = isPlainObject(readModel.dashboard) ? readModel.dashboard : {};
  const resultsHref = enrichResultsUrlWithStoredDispatchContext(
    normalizeText(readModel.latest_optimization?.href) || appendDispatchDateToUrl("/results"),
  );
  const dashboardStatus = normalizeText(readModel.dashboard_status).toLowerCase() === "empty" ? "empty" : "loaded";
  const dashboardNotice = normalizeText(readModel.dashboard_notice) || null;
  const missions = Array.isArray(rawDashboard.missions) ? rawDashboard.missions : [];
  const orders = resolveOrdersData(rawDashboard, missions.length);
  const trucks = resolveTrucksData(rawDashboard);
  const tonnage = resolveTonnageData(rawDashboard, missions, trucks);
  const alerts = resolveAlertsData(rawDashboard, dashboardNotice);
  const analytics = resolveAnalyticsData(readModel, trucks.total);
  const lastOptimization = resolveLastOptimization(rawDashboard, readModel, missions.length, trucks.total);
  const lastOptimizationDisplay = resolveOptimizationStatusPresentation(lastOptimization?.status, {
    hasOptimization: Boolean(lastOptimization),
  });
  const optimizationTimestamp = formatOptimizationTimestamp(lastOptimization?.calculated_at);
  const optimizationLabelPrefix = "Dernière optimisation (clic sur Optimiser ou mise à jour du planning via Réaffecter, Suspendues, etc.)";
  const optimizationLabel = optimizationTimestamp
    ? optimizationTimestamp.time
      ? `${optimizationLabelPrefix} : ${optimizationTimestamp.date} à ${optimizationTimestamp.time}`
      : `${optimizationLabelPrefix} : ${optimizationTimestamp.date}`
    : "Aucune optimisation disponible";
  const lastOptimizationSummary = {
    text: optimizationTimestamp?.dayMonth
      ? `Dernière optimisation le ${optimizationTimestamp.dayMonth}${optimizationTimestamp.time ? ` à ${optimizationTimestamp.time}` : ""}`
      : lastOptimization ? "Dernière optimisation : heure inconnue" : "Aucune optimisation disponible",
    tone: lastOptimization ? lastOptimizationDisplay.tone : "neutral",
    // The two removed header pills carried the state; keep it next to the date when it needs attention.
    stateLabel: lastOptimization && lastOptimizationDisplay.tone !== "success" ? lastOptimizationDisplay.label : null,
  };

  return {
    alerts,
    analytics,
    dashboardNotice,
    dashboardStatus,
    kpis: buildKpis({ alerts, orders, tonnage, trucks }),
    lastOptimization,
    lastOptimizationDisplay,
    lastOptimizationSummary,
    optimizationLabel,
    optimizationStatusTone: lastOptimizationDisplay.tone === "warning"
      ? "bg-[rgba(180,123,32,0.14)] text-[#9a6700]"
      : lastOptimizationDisplay.tone === "neutral"
        ? "bg-[rgba(100,116,139,0.14)] text-[#475569]"
        : "bg-[rgba(58,125,68,0.14)] text-[#3a7d44]",
    orders,
    rawDashboard,
    readModel,
    resultsHref,
    tonnage,
    trucks,
  };
}
